import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { blogCategories } from '../../data/blog-articles';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import BlogSEO from '../../components/BlogSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogOverview = () => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [blogArticles, setBlogArticles] = useState([]);

  useEffect(() => {
    // Fetch articles from database
    const fetchArticles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/articles?status=published`);
        if (res.ok) {
          const data = await res.json();
          // Transform DB articles to match expected format
          const transformed = data.map(a => ({
            ...a,
            titleEn: a.title_en || a.title,
            excerptEn: a.excerpt_en || a.excerpt,
            categoryNameEn: a.category_name_en || a.category_name,
            featuredImage: a.featured_image,
            readingTime: a.reading_time,
            publishedAt: a.published_at || a.created_at
          }));
          setBlogArticles(transformed);
        }
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <BlogSEO type="overview" />
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <Header />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'de' ? 'Blog & Ratgeber' : 'Blog & Guide'}
              </h1>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'de' 
                  ? 'Expertenwissen rund um Haartransplantation, Barttransplantation und Haarausfall. Fundierte Informationen von unseren Spezialisten.'
                  : 'Expert knowledge about hair transplantation, beard transplantation and hair loss. Well-founded information from our specialists.'}
              </p>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <Link
                to="/blog"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isDark 
                    ? 'bg-[#7BA7C2] text-white' 
                    : 'bg-[#7BA7C2] text-white'
                }`}
              >
                {language === 'de' ? 'Alle Artikel' : 'All Articles'}
              </Link>
              {blogCategories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/blog/kategorie/${category.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isDark 
                      ? 'bg-white/10 text-white hover:bg-[#7BA7C2]/30' 
                      : 'bg-gray-200 text-gray-700 hover:bg-[#7BA7C2]/20'
                  }`}
                >
                  {language === 'de' ? category.name : category.nameEn}
                </Link>
              ))}
            </div>

            {/* Featured Article */}
            {blogArticles[0] && (
              <Link 
                to={`/blog/${blogArticles[0].slug}`}
                className={`block mb-12 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  isDark ? 'bg-white/5' : 'bg-white shadow-lg'
                }`}
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="aspect-video md:aspect-auto">
                    <img 
                      src={blogArticles[0].featuredImage} 
                      alt={language === 'de' ? blogArticles[0].title : blogArticles[0].titleEn}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:p-10 flex flex-col justify-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 w-fit ${
                      isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#7BA7C2]/10 text-[#5A8BA6]'
                    }`}>
                      {language === 'de' ? blogArticles[0].categoryName : blogArticles[0].categoryNameEn}
                    </span>
                    <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'de' ? blogArticles[0].title : blogArticles[0].titleEn}
                    </h2>
                    <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {language === 'de' ? blogArticles[0].excerpt : blogArticles[0].excerptEn}
                    </p>
                    <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(blogArticles[0].publishedAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {blogArticles[0].readingTime} min
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Article Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogArticles.slice(1).map((article) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className={`group rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    isDark ? 'bg-white/5' : 'bg-white shadow-md'
                  }`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={article.featuredImage} 
                      alt={language === 'de' ? article.title : article.titleEn}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#7BA7C2]/10 text-[#5A8BA6]'
                    }`}>
                      {language === 'de' ? article.categoryName : article.categoryNameEn}
                    </span>
                    <h3 className={`text-lg font-bold mb-2 line-clamp-2 group-hover:text-[#7BA7C2] transition-colors ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {language === 'de' ? article.title : article.titleEn}
                    </h3>
                    <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {language === 'de' ? article.excerpt : article.excerptEn}
                    </p>
                    <div className={`flex items-center justify-between text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.publishedAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readingTime} min
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* CTA Section */}
            <div className={`mt-16 text-center p-10 rounded-2xl ${
              isDark ? 'bg-gradient-to-r from-[#7BA7C2]/20 to-[#5A8BA6]/20' : 'bg-gradient-to-r from-[#7BA7C2]/10 to-[#5A8BA6]/10'
            }`}>
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'de' ? 'Haben Sie Fragen?' : 'Have Questions?'}
              </h2>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'de' 
                  ? 'Vereinbaren Sie eine kostenlose Beratung mit unseren Experten.'
                  : 'Schedule a free consultation with our experts.'}
              </p>
              <Link
                to="/#kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#7BA7C2] text-white rounded-full font-medium hover:bg-[#5A8BA6] transition-colors"
              >
                {language === 'de' ? 'Kostenlose Beratung' : 'Free Consultation'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogOverview;
